// src/app/guards/token-align.guard.ts
import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './service/auth.service';
import { FreelancerDataService } from '../../services/freelancer-data.service';

type CommunityLookup = { data?: { space_id?: number; Spaces?: { id?: number } } };
const bySlug = (slug: string) => `${environment.apiUrl}/communities/${encodeURIComponent(slug)}`;

function readResolvedSpaceId(route: ActivatedRouteSnapshot): number {
  const fromThis =
    Number((route.data as any)?.space?.space_id || (route.data as any)?.space?.id || 0) || 0;
  if (fromThis) return fromThis;
  const parent = route.parent;
  if (!parent) return 0;
  return Number((parent.data as any)?.space?.space_id || (parent.data as any)?.space?.id || 0) || 0;
}

export const TokenAlignGuard: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const http = inject(HttpClient);
  const auth = inject(AuthService);
  const api = inject(FreelancerDataService);

  const slug = (route.paramMap.get('slug') || '').trim();
  if (!slug) return true;
  if (!auth.isLoggedIn()) return true;

  let targetSpaceId = readResolvedSpaceId(route);
  if (!targetSpaceId) {
    const cacheKey = `spaceBySlug:${slug}`;
    targetSpaceId = Number(sessionStorage.getItem(cacheKey) || '0') || 0;
    if (!targetSpaceId) {
      try {
        const res = await firstValueFrom(http.get<CommunityLookup>(bySlug(slug)));
        targetSpaceId = Number(res?.data?.space_id || res?.data?.Spaces?.id || 0) || 0;
        if (targetSpaceId) sessionStorage.setItem(cacheKey, String(targetSpaceId));
      } catch {
        return true;
      }
    }
  }
  if (!targetSpaceId) return true;

  const currentSpaceId = Number(localStorage.getItem('spaceID') || '0') || auth.getSpaceIdFromToken() || 0;
  if (currentSpaceId === targetSpaceId) return true;

  const userId = auth.getUserId();
  if (!userId) return true;

  sessionStorage.setItem('switchingCommunity', '1');
  sessionStorage.setItem('pendingSpaceID', String(targetSpaceId));
  try {
    const resp: any = await firstValueFrom(api.switchCommunityToken({ user_id: +userId, space_id: +targetSpaceId }));
    const token = resp?.data?.token;
    const sid = Number(resp?.data?.space_id || targetSpaceId) || 0;
    if (token && sid) {
      auth.setToken(token);
      localStorage.setItem('spaceID', String(sid));
    }
  } catch {
  } finally {
    sessionStorage.removeItem('pendingSpaceID');
    sessionStorage.removeItem('switchingCommunity');
  }

  return true;
};
